import { CircularProgress } from "@material-ui/core";
import {
  CenterFocusStrong as CenterFocusStrongIcon,
  Check as CheckIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  DragIndicator as DragIndicatorIcon,
  Room as RoomIcon,
  Search as SearchIcon,
  Speed as SpeedIcon,
  Waves as WavesIcon,
} from "@material-ui/icons";
import { Reorder } from "framer-motion";
import L from "leaflet";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { alignLng, geoJsonToLines, repeatOnWorldCopies, unwrapLines } from "../../../../util/worldCopies";
import * as actions from "../actions";
import { zoneOverlays } from "../../../../util/zoneOverlays";
import {
  addPortDistanceForDistanceMatrix,
  convertPortOperationToDistanceMatrixCalc,
  getGeoJsonLineAndCoordinates,
} from "../dataMapping";

export default function PortDistance(props) {
  const { setLoader = () => { }, loader, topTabs } = props;

  const [addPortsArray, setAddPortsArray] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [getDistance, setDistance] = useState([]);
  const [getAvoidPanama, setAvoidPanama] = useState(false);
  const [getAvoidSuez, setAvoidSuez] = useState(false);
  const [speed, setSpeed] = useState("13.0");
  const [seaMargin, setSeaMargin] = useState("0.0");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const [mapRef, routeLayerGroupRef, inputRef] = [useRef(null), useRef(null), useRef(null)];
  const routeBoundsRef = useRef(null);
  const detachRouteCopiesRef = useRef(() => { });
  const [editingPortId, setEditingPortId] = useState(null);
  const [editingSource, setEditingSource] = useState(null); // 'top' | 'sidebar'
  const [editQuery, setEditQuery] = useState("");
  const [editSuggestions, setEditSuggestions] = useState([]);
  const [isEditSearching, setIsEditSearching] = useState(false);
  const [isEditDropdownOpen, setIsEditDropdownOpen] = useState(false);

  const editInputRef = useRef(null);
  const sidebarEditInputRef = useRef(null);
  const editChipContainerRef = useRef(null);
  const editSidebarContainerRef = useRef(null);

  const markersvg = (num, portName = "", country = "", isFirst = false, isLast = false) => {
    const pinColor = isFirst ? "#1e40af" : isLast ? "#065f46" : "#1f2937";
    const accentColor = isFirst ? "#3b82f6" : isLast ? "#10b981" : "#64748b";
    const roleLabel = isFirst ? "DEP" : isLast ? "DEST" : `#${num}`;
    const roleClass = isFirst ? "is-start" : isLast ? "is-end" : "is-waypoint";
    const displayName = portName || `Port ${num}`;

    return `
      <div class="maritime-marker-root ${roleClass}">
        <!-- 1. Sonar Radar Pulse Radiating from Anchor on Water -->
        <div class="maritime-sonar-emitter">
          <div class="sonar-wave wave-1" style="border-color: ${accentColor};"></div>
          <div class="sonar-wave wave-2" style="border-color: ${accentColor};"></div>
          <div class="sonar-beacon-dot" style="background: ${accentColor};"></div>
        </div>

        <!-- 2. Attached Port Name Pill Label -->
        <div class="maritime-pin-label-card">
          <span class="pin-role-tag ${roleClass}">${roleLabel}</span>
          <span class="pin-port-title" title="${displayName}">${displayName}</span>
          ${country ? `<span class="pin-country-badge">${country}</span>` : ""}
        </div>

        <!-- 3. Navigation Teardrop Pin -->
        <div class="maritime-pin-stem">
          <svg
            width="28"
            height="38"
            viewBox="0 0 60 81"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="maritime-pin-vector"
          >
            <path
              d="M30.0336 80.2488C29.0354 80.2488 28.124 79.8148 27.473 79.0336C21.0496 71.0043 0 43.7918 0 29.9902C0 13.4544 13.4544 0 29.9902 0C46.5261 0 59.9804 13.4544 59.9804 29.9902C59.9804 43.8352 38.9743 71.0477 32.5075 79.0336C31.9433 79.8148 31.0318 80.2488 30.0336 80.2488ZM30.0336 6.72718C17.2303 6.72718 6.81398 17.1435 6.81398 29.9468C6.81398 42.7502 17.2303 53.1664 30.0336 53.1664C42.837 53.1664 53.2533 42.7502 53.2533 29.9468C53.2533 17.1435 42.837 6.72718 30.0336 6.72718Z"
              fill="${pinColor}"
            />
            <path
              d="M30.0318 49.2165C40.6744 49.2165 49.302 40.589 49.302 29.9464C49.302 19.3038 40.6744 10.6763 30.0318 10.6763C19.3892 10.6763 10.7617 19.3038 10.7617 29.9464C10.7617 40.589 19.3892 49.2165 30.0318 49.2165Z"
              fill="white"
            />
            <circle cx="30" cy="30" r="15" fill="${accentColor}" fill-opacity="0.15" />
            <text
              x="30"
              y="32"
              fill="${pinColor}"
              font-weight="600"
              text-anchor="middle"
              dominant-baseline="central"
              font-family="Inter, -apple-system, BlinkMacSystemFont, sans-serif"
              font-size="20"
            >
              ${num}
            </text>
          </svg>
        </div>
      </div>
    `;
  };

  useEffect(() => {
    const defaultMapCenter = new L.LatLng(20, 0);
    const defaultZoom = 3;

    const seaDefault = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
    );
    const seaMark = new L.TileLayer(
      "http://t1.openseamap.org/seamark/{z}/{x}/{y}.png"
    );
    const seaProfil = new L.TileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}"
    );
    const osm = new L.tileLayer("http://{s}.tile.osm.org/{z}/{x}/{y}.png");
    const Esri_WorldImagery = L.tileLayer(
      "http://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
    );
    const Esri_WorldGrayCanvas = L.tileLayer(
      "http://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}"
    );
    const OpenStreetMap_BlackAndWhite = L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png"
    );

    const map = new L.Map("map", {
      layers: [seaDefault, seaMark],
      center: defaultMapCenter,
      zoom: defaultZoom,
      editable: true,
      minZoom: 2,
      cursor: true,
    });

    mapRef.current = map;
    routeLayerGroupRef.current = L.featureGroup().addTo(map);

    map.on("baselayerchange", function () {
      seaMark.bringToFront();
    });

    const baseMaps = {
      Default: seaDefault,
      "Sea profile": seaProfil,
      Imagery: Esri_WorldImagery,
      "Open Street Map": osm,
      Gray: Esri_WorldGrayCanvas,
      "Black & white": OpenStreetMap_BlackAndWhite,
    };

    // Position layer control at bottomright so it NEVER overlaps with the top-right panel
    L.control.layers(baseMaps, zoneOverlays(map), { position: "bottomright" }).addTo(map);

    // Zoom control at bottomleft to avoid colliding with top bar and right floating panel
    map.zoomControl.setPosition("bottomleft");

    const timer = setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 200);

    return () => {
      clearTimeout(timer);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Invalidate map size whenever sidebar collapses/expands or container size changes
  useEffect(() => {
    const t1 = setTimeout(() => {
      if (mapRef.current) mapRef.current.invalidateSize();
    }, 50);
    const t2 = setTimeout(() => {
      if (mapRef.current) mapRef.current.invalidateSize();
    }, 150);
    const t3 = setTimeout(() => {
      if (mapRef.current) mapRef.current.invalidateSize();
    }, 260);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [isSidebarCollapsed]);

  useEffect(() => {
    const mapEl = document.getElementById("map");
    if (!mapEl || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    });
    observer.observe(mapEl);
    return () => observer.disconnect();
  }, []);

  const debouncedPortSearch = useRef(
    _.debounce((query) => {
      actions
        .portsByChar(query)
        .then((res) => {
          setIsSearching(false);
          const list = (Array.isArray(res?.data?.list) && res.data.list) || [];
          setSearchSuggestions(list);
        })
        .catch(() => {
          setIsSearching(false);
        });
    }, 300)
  ).current;

  const debouncedEditPortSearch = useRef(
    _.debounce((query) => {
      actions
        .portsByChar(query)
        .then((res) => {
          setIsEditSearching(false);
          const list = (Array.isArray(res?.data?.list) && res.data.list) || [];
          setEditSuggestions(list);
        })
        .catch(() => {
          setIsEditSearching(false);
        });
    }, 250)
  ).current;

  const onEditSearchInput = (value) => {
    setEditQuery(value);
    if (value?.trim().length > 1) {
      setIsEditSearching(true);
      setIsEditDropdownOpen(true);
      debouncedEditPortSearch(value.trim());
    } else {
      setEditSuggestions([]);
      setIsEditDropdownOpen(false);
    }
  };

  const startEditingPort = (loc, source = "top") => {
    setEditingPortId(loc.id);
    setEditingSource(source);
    setEditQuery(loc.port || "");
    setEditSuggestions([]);
    setIsEditSearching(false);
    setIsEditDropdownOpen(false);
    if (loc.port && loc.port.trim().length > 1) {
      setIsEditSearching(true);
      setIsEditDropdownOpen(true);
      debouncedEditPortSearch(loc.port.trim());
    }
  };

  const cancelEditing = () => {
    setEditingPortId(null);
    setEditingSource(null);
    setEditQuery("");
    setEditSuggestions([]);
    setIsEditDropdownOpen(false);
  };

  const handleUpdatePort = (portId, newPortItem) => {
    const updated = addPortsArray.map((p) => {
      if (p.id === portId) {
        return {
          ...p,
          port: newPortItem.name,
          selectedPort: newPortItem,
          lat: newPortItem.lat || "",
          lng: newPortItem.lng || "",
        };
      }
      return p;
    });

    setAddPortsArray(updated);
    cancelEditing();

    const validPorts = updated.filter(
      (p) => !!p.port && p.lat !== "" && p.lng !== ""
    );

    if (validPorts.length >= 2) {
      renderRouteOnMap(validPorts);
      calculateRouteForPorts(updated);
    } else {
      renderRouteOnMap(validPorts);
      setDistance([]);
    }
  };

  useEffect(() => {
    if (!editingPortId) return;

    const handleClickOutside = (e) => {
      const isInsideChip =
        editChipContainerRef.current &&
        editChipContainerRef.current.contains(e.target);
      const isInsideSidebar =
        editSidebarContainerRef.current &&
        editSidebarContainerRef.current.contains(e.target);

      if (!isInsideChip && !isInsideSidebar) {
        cancelEditing();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [editingPortId]);

  useEffect(() => {
    if (editingPortId && editingSource === "top" && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    } else if (editingPortId && editingSource === "sidebar" && sidebarEditInputRef.current) {
      sidebarEditInputRef.current.focus();
      sidebarEditInputRef.current.select();
    }
  }, [editingPortId, editingSource]);

  const onSearchInput = (value) => {
    setSearchQuery(value);
    if (value?.trim().length > 1) {
      setIsSearching(true);
      setIsDropdownOpen(true);
      debouncedPortSearch(value.trim());
    } else {
      setSearchSuggestions([]);
      setIsDropdownOpen(false);
    }
  };

  const calculateRouteForPorts = async (
    portsList,
    avoidPanamaVal = getAvoidPanama,
    avoidSuezVal = getAvoidSuez
  ) => {
    const validPorts = (portsList || []).filter(
      (p) => !!p.port && p.lat !== "" && p.lng !== ""
    );

    if (validPorts.length < 2) {
      setDistance([]);
      renderRouteOnMap(validPorts);
      return;
    }

    setLoader(true);
    try {
      const { routeList } = await convertPortOperationToDistanceMatrixCalc(validPorts);
      const disOutput = await actions.getPortDistanceMatrix(routeList, {
        panama: avoidPanamaVal,
        suez: avoidSuezVal,
      });

      const extractedData = [];
      for (const key in disOutput?.data) {
        extractedData.push({
          distEca: disOutput?.data?.[key]?.distEca || 0,
          distNormal: disOutput?.data?.[key]?.distNormal || 0,
          name: key,
          geometry: disOutput?.data?.[key]?.geometry,
        });
      }
      setDistance(extractedData);

      const newPortOperations = addPortDistanceForDistanceMatrix(
        disOutput?.data,
        validPorts,
        true
      );
      renderRouteOnMap(newPortOperations);
    } catch (err) {
      console.error("Error calculating route:", err);
    } finally {
      setLoader(false);
    }
  };

  const handleSelectPort = (item) => {
    const newPort = {
      id: uuidv4(),
      port: item.name,
      selectedPort: item,
      lat: item.lat || "",
      lng: item.lng || "",
    };
    const updated = [...addPortsArray, newPort];
    setAddPortsArray(updated);
    setSearchQuery("");
    setSearchSuggestions([]);
    setIsDropdownOpen(false);

    if (updated.length >= 2) {
      calculateRouteForPorts(updated);
    } else {
      renderRouteOnMap(updated);
    }

    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleRemovePort = (portId) => {
    const updated = addPortsArray.filter((p) => p.id !== portId);
    setAddPortsArray(updated);

    if (updated.length >= 2) {
      calculateRouteForPorts(updated);
    } else {
      setDistance([]);
      renderRouteOnMap(updated);
    }
  };

  const handleClearAll = () => {
    setAddPortsArray([]);
    setDistance([]);
    setSearchQuery("");
    setSearchSuggestions([]);
    setIsDropdownOpen(false);
    if (routeLayerGroupRef.current) {
      routeLayerGroupRef.current.clearLayers();
      detachRouteCopiesRef.current();
    }
  };

  const addPortsArrayRef = useRef(addPortsArray);
  useEffect(() => {
    addPortsArrayRef.current = addPortsArray;
  }, [addPortsArray]);

  // Framer Motion Reorder handlers with animated physical repositioning
  const handleReorder = (newOrder) => {
    addPortsArrayRef.current = newOrder;
    setAddPortsArray(newOrder);
  };

  const handleDragEnd = () => {
    const currentPorts = addPortsArrayRef.current;
    const validPorts = currentPorts.filter(
      (p) => !!p.port && p.lat !== "" && p.lng !== ""
    );
    if (validPorts.length >= 2) {
      // Instantly update pin numbering (1, 2, 3...) on Leaflet map
      renderRouteOnMap(validPorts);
      // Automatically recalculate maritime route for new sequence
      calculateRouteForPorts(currentPorts);
    } else if (validPorts.length > 0) {
      renderRouteOnMap(validPorts);
    }
  };

  const handleToggleSuez = () => {
    const nextVal = !getAvoidSuez;
    setAvoidSuez(nextVal);
    if (addPortsArray.length >= 2) {
      calculateRouteForPorts(addPortsArray, getAvoidPanama, nextVal);
    }
  };

  const handleTogglePanama = () => {
    const nextVal = !getAvoidPanama;
    setAvoidPanama(nextVal);
    if (addPortsArray.length >= 2) {
      calculateRouteForPorts(addPortsArray, nextVal, getAvoidSuez);
    }
  };

  const handleSpeedStep = (delta) => {
    const current = parseFloat(speed) || 13.0;
    const nextVal = Math.max(1, +(current + delta).toFixed(1));
    setSpeed(String(nextVal));
  };

  const handleMarginStep = (delta) => {
    const current = parseFloat(seaMargin) || 0.0;
    const nextVal = Math.max(0, +(current + delta).toFixed(1));
    setSeaMargin(String(nextVal));
  };

  const renderRouteOnMap = (ports = []) => {
    const map = mapRef.current;
    if (!map || !routeLayerGroupRef.current) return;

    detachRouteCopiesRef.current();
    routeLayerGroupRef.current.clearLayers();
    const group = routeLayerGroupRef.current;

    // One continuous track across the antimeridian, repeated on every world copy
    const lines = unwrapLines(
      geoJsonToLines(ports.filter((p) => p.geoJSON).map((p) => getGeoJsonLineAndCoordinates(p.geoJSON)))
    );
    const portLngs = ports.map((p) => alignLng(p.lng, lines));

    const bounds = L.latLngBounds([]);
    // Draws one copy of the route shifted by `offset` degrees (one per repeated world)
    const drawOffset = (offset) => {
      for (let i = 0; i < ports.length; i++) {
        const pt = ports[i];
        const lat = Number(pt.lat) || 0;
        const lng = Number(pt.lng) || 0;
        const mapLng = portLngs[i] + offset;
        const isFirst = i === 0;
        const isLast = i === ports.length - 1 && ports.length > 1;

        const svgIcon = L.divIcon({
          html: markersvg(i + 1, pt.port, pt.selectedPort?.country, isFirst, isLast),
          className: "maritime-custom-div-icon",
          iconSize: [0, 0],
          iconAnchor: [0, 0],
          popupAnchor: [0, -74],
        });

        const roleBadge = isFirst
          ? "Departure Port"
          : isLast
            ? "Destination Port"
            : `Waypoint ${i + 1}`;

        const popupHtml = `
        <div class="maritime-popup-card">
          <div class="popup-top">
            <span class="popup-role ${isFirst ? "is-start" : isLast ? "is-end" : ""}">${roleBadge}</span>
            <span class="popup-index">#${i + 1}</span>
          </div>
          <div class="popup-title">${pt.port}</div>
          ${pt.selectedPort?.country ? `<div class="popup-country">${pt.selectedPort.country}</div>` : ""}
          <div class="popup-geo">
            <span>${Math.abs(lat).toFixed(3)}° ${lat >= 0 ? "N" : "S"}</span>
            <span class="sep">•</span>
            <span>${Math.abs(lng).toFixed(3)}° ${lng >= 0 ? "E" : "W"}</span>
          </div>
        </div>
      `;

        // Marker is fixed pin (dragging pins on map is disabled)
        const marker = L.marker([lat, mapLng], {
          icon: svgIcon,
          draggable: false,
        }).bindPopup(popupHtml, {
          className: "maritime-leaflet-popup",
          closeButton: true,
          autoPan: true,
        });

        group.addLayer(marker);
        if (offset === 0) bounds.extend([lat, mapLng]);
      }

      if (lines.length > 0) {
        const combinedGeoJson = {
          type: "MultiLineString",
          coordinates: lines.map((line) => line.map(([x, y]) => [x + offset, y])),
        };
        if (offset === 0) lines.flat().forEach(([x, y]) => bounds.extend([y, x]));
        // Layer 1: Wide Nautical Corridor Glow
        const glowLayer = L.geoJSON(combinedGeoJson, {
          style: {
            color: "#38bdf8",
            weight: 9,
            opacity: 0.28,
            className: "maritime-corridor-glow",
            lineCap: "round",
            lineJoin: "round",
          },
        });
        group.addLayer(glowLayer);

        // Layer 2: Base High-Contrast Navigation Track
        const baseLayer = L.geoJSON(combinedGeoJson, {
          style: {
            color: "#1e3a8a",
            weight: 3.5,
            opacity: 0.9,
            className: "maritime-route-base",
            lineCap: "round",
            lineJoin: "round",
          },
        });
        group.addLayer(baseLayer);

        // Layer 3: Dynamic Animated Vessel Transit Flow
        const animatedFlowLayer = L.geoJSON(combinedGeoJson, {
          style: {
            color: "#93c5fd",
            weight: 2.5,
            opacity: 0.95,
            dashArray: "8, 14",
            className: "maritime-route-animated-flow",
            lineCap: "round",
            lineJoin: "round",
          },
        });
        group.addLayer(animatedFlowLayer);
      }
    };

    detachRouteCopiesRef.current = repeatOnWorldCopies(
      map,
      [...lines.flat().map(([x]) => x), ...portLngs],
      drawOffset
    );

    routeBoundsRef.current = bounds;
    if (bounds.isValid()) {
      map.flyToBounds(bounds, {
        paddingTopLeft: [50, 50],
        paddingBottomRight: [50, 50],
        duration: 1.2,
        easeLinearity: 0.25,
        maxZoom: 8,
      });
    }
  };

  const handleFitRouteBounds = () => {
    if (mapRef.current && routeLayerGroupRef.current) {
      const bounds = routeBoundsRef.current;
      if (bounds && bounds.isValid()) {
        mapRef.current.flyToBounds(bounds, {
          paddingTopLeft: [50, 50],
          paddingBottomRight: [50, 50],
          duration: 0.9,
          easeLinearity: 0.25,
          maxZoom: 8,
        });
      }
    }
  };

  const handleRouteCalculation = () => {
    calculateRouteForPorts(addPortsArray);
  };

  const handleResetRoute = () => {
    setDistance([]);
    if (routeLayerGroupRef.current) {
      routeLayerGroupRef.current.clearLayers();
      detachRouteCopiesRef.current();
    }
  };

  // Calculations
  const numSpeed = parseFloat(speed) > 0 ? parseFloat(speed) : 13.0;
  const numSeaMargin = !isNaN(parseFloat(seaMargin)) ? parseFloat(seaMargin) : 0.0;

  const totalDistEca =
    getDistance?.reduce((sum, item) => sum + (Number(item?.distEca) || 0), 0) || 0;
  const totalDistNormal =
    getDistance?.reduce((sum, item) => sum + (Number(item?.distNormal) || 0), 0) || 0;
  const totalDistance = totalDistNormal;

  const calculateDurationDays = (dist) => {
    if (!dist || numSpeed <= 0) return 0;
    const effectiveDist = dist * (1 + numSeaMargin / 100);
    return effectiveDist / (numSpeed * 24);
  };

  const formatDays = (days) => {
    if (!days || isNaN(days)) return "0.0";
    return days.toFixed(1);
  };

  const formatDurationBreakdown = (days) => {
    if (!days || isNaN(days)) return "";
    const d = Math.floor(days);
    const h = Math.round((days - d) * 24);
    return `${d}d ${h}h`;
  };

  const getPortCountryCode = (loc) => {
    if (loc?.selectedPort?.country_code) return loc.selectedPort.country_code.toUpperCase();
    if (loc?.selectedPort?.code) return loc.selectedPort.code.toUpperCase();
    const c = loc?.selectedPort?.country || "";
    if (!c) return "PT";
    const lower = c.toLowerCase();
    if (lower === "india") return "IN";
    if (lower.includes("united states") || lower === "usa" || lower === "us") return "US";
    if (lower.includes("emirates") || lower === "uae") return "AE";
    if (lower === "japan") return "JP";
    if (lower === "australia") return "AU";
    if (lower === "china") return "CN";
    if (lower === "singapore") return "SG";
    if (lower === "netherlands") return "NL";
    if (lower === "germany") return "DE";
    if (lower.includes("kingdom") || lower === "uk") return "GB";
    if (lower === "spain") return "ES";
    if (lower === "france") return "FR";
    if (lower === "italy") return "IT";
    if (lower === "canada") return "CA";
    if (lower === "brazil") return "BR";
    if (lower === "south africa") return "ZA";
    if (c.length >= 2) return c.slice(0, 2).toUpperCase();
    return "PT";
  };

  const getPortDisplayName = (loc) => {
    const portName = loc?.port || "Selected Port";
    const country = loc?.selectedPort?.country || "";
    return country ? `${portName}, ${country}` : portName;
  };

  const totalDays = calculateDurationDays(totalDistance);
  const totalDaysEca = calculateDurationDays(totalDistEca);
  // distNormal is the total distance; non-ECA = total - ECA
  const totalDistNonEca = Math.max(0, totalDistance - totalDistEca);
  const totalDaysNonEca = calculateDurationDays(totalDistNonEca);
  const ecaPercent = totalDistance > 0 ? ((totalDistEca / totalDistance) * 100).toFixed(1) : "0.0";
  const nonEcaPercent = totalDistance > 0 ? ((totalDistNonEca / totalDistance) * 100).toFixed(1) : "0.0";

  const hasCalculatedRoute = getDistance.length > 0;
  const validPorts = addPortsArray.filter((p) => !!p.port);
  const validPortsCount = validPorts.length;

  return (
    <div className="w-full h-full flex flex-row overflow-hidden bg-white">
      {/* Left-Hand Sidebar (Signal Ocean Style) */}
      <div
        className={`signal-ocean-panel ${isSidebarCollapsed ? "collapsed" : ""}`}
        onClick={isSidebarCollapsed ? () => setIsSidebarCollapsed(false) : undefined}
        title={isSidebarCollapsed ? "Expand sidebar" : undefined}
      >
        {/* Panel Header */}
        <div
          className="panel-header"
          onClick={isSidebarCollapsed ? () => setIsSidebarCollapsed(false) : undefined}
        >
          {!isSidebarCollapsed && (
            <div className="flex items-center gap-2 overflow-hidden">
              <span className="font-medium text-[11px] uppercase tracking-wider whitespace-nowrap" style={{ color: '#475569' }}>
                Route Summary
              </span>
              {hasCalculatedRoute && (
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap inline-flex items-center leading-none" style={{ background: '#f3f4f6', color: '#475569', border: '1px solid #d1d5db' }}>
                  Calculated
                </span>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsSidebarCollapsed(!isSidebarCollapsed);
            }}
            className={isSidebarCollapsed ? "panel-toggle-collapsed" : "panel-toggle-btn"}
            title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isSidebarCollapsed ? (
              <ChevronRightIcon style={{ fontSize: 18 }} />
            ) : (
              <>
                <span className="toggle-label">Hide</span>
                <ChevronLeftIcon className="toggle-icon" style={{ fontSize: 14 }} />
              </>
            )}
          </button>
        </div>

        {isSidebarCollapsed ? (
          <div
            className="flex-1 flex items-center justify-center cursor-pointer transition-colors py-4"
            style={{ background: '' }}
            onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
            onMouseLeave={e => e.currentTarget.style.background = ''}
            onClick={() => setIsSidebarCollapsed(false)}
            title="Expand Route Summary"
          >
            <span
              style={{
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
                fontSize: "11px",
                letterSpacing: "0.1em",
                fontWeight: 500,
                color: "#6b7280",
                textTransform: "uppercase",
                userSelect: "none",
              }}
            >
              Route Summary
            </span>
          </div>
        ) : (
          <>
            <div className="panel-body">
              {/* 1. PORT LOCATIONS Card */}
              <div className="voyage-port-locations-card">
                <div className="card-header-row">
                  <div className="card-header-left">
                    <RoomIcon style={{ fontSize: 13, color: '#64748b' }} />
                    <span>PORT LOCATIONS</span>
                  </div>
                  <span className="card-ports-badge">{validPorts.length} Ports</span>
                </div>

                {/* Scroll area constrained with max-height so it never exceeds sidebar */}
                <div className="port-locations-scroll-area">
                  <Reorder.Group
                    axis="y"
                    values={addPortsArray}
                    onReorder={handleReorder}
                    as="div"
                    className="port-timeline-track"
                  >
                    {addPortsArray.map((loc, idx, arr) => {
                      const isFirst = idx === 0;
                      const isLast = idx === arr.length - 1;
                      const leg = getDistance?.[idx];
                      const legDistNormal = Number(leg?.distNormal) || 0;
                      const legDistEca = Number(leg?.distEca) || 0;
                      const legTotalDist = legDistNormal;
                      const legDays = calculateDurationDays(legTotalDist);
                      const countryCode = getPortCountryCode(loc);
                      const displayName = getPortDisplayName(loc);

                      return (
                        <Reorder.Item
                          key={loc.id || idx}
                          value={loc}
                          as="div"
                          className="port-timeline-row"
                          whileDrag={{
                            scale: 1.02,
                            boxShadow: "0 4px 14px rgba(0, 0, 0, 0.1)",
                            backgroundColor: "#f9fafb",
                            zIndex: 100,
                            cursor: "grabbing",
                          }}
                          onDragEnd={handleDragEnd}
                        >
                          {/* Timeline Rail on Left */}
                          <div className="timeline-rail">
                            <div
                              className={`node-dot ${isFirst ? "start" : isLast ? "end" : "waypoint"
                                }`}
                            />
                            {!isLast && <div className="rail-connector" />}
                          </div>

                          {/* Port Item Content */}
                          <div className="port-item-content">
                            {editingPortId === loc.id && editingSource === "sidebar" ? (
                              <div
                                ref={editSidebarContainerRef}
                                className="port-box-pill is-editing"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <DragIndicatorIcon style={{ fontSize: 14, color: '#9ca3af', cursor: 'grab', marginRight: 2 }} />
                                <span className="country-flag-badge">{countryCode}</span>
                                <input
                                  ref={sidebarEditInputRef}
                                  type="text"
                                  className="sidebar-port-edit-input flex-1 bg-transparent border-none outline-none text-[11px] text-slate-800"
                                  value={editQuery}
                                  onChange={(e) => onEditSearchInput(e.target.value)}
                                  onFocus={() => {
                                    if (editSuggestions.length > 0) setIsEditDropdownOpen(true);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Escape") {
                                      cancelEditing();
                                    } else if (e.key === "Enter" && editSuggestions.length > 0) {
                                      handleUpdatePort(loc.id, editSuggestions[0]);
                                    }
                                  }}
                                  placeholder="Search port..."
                                />
                                <button
                                  type="button"
                                  className="port-box-remove"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    cancelEditing();
                                  }}
                                  title="Cancel edit"
                                >
                                  <CloseIcon style={{ fontSize: 13 }} />
                                </button>

                                {isEditDropdownOpen && (
                                  <div className="signal-ocean-suggestions-dropdown sidebar-dropdown">
                                    {isEditSearching ? (
                                      <div className="flex items-center gap-1.5 p-2 text-[11px] font-medium text-slate-500">
                                        <CircularProgress size={12} color="inherit" />
                                        <span>Searching ports...</span>
                                      </div>
                                    ) : editSuggestions.length > 0 ? (
                                      editSuggestions.map((item, sIdx) => (
                                        <div
                                          key={sIdx}
                                          className="suggestion-item"
                                          onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleUpdatePort(loc.id, item);
                                          }}
                                        >
                                          <RoomIcon style={{ fontSize: 13, color: "#64748b" }} />
                                          <span className="suggestion-port-name">{item.name}</span>
                                          {item.country && (
                                            <span className="suggestion-port-country">
                                              ({item.country})
                                            </span>
                                          )}
                                        </div>
                                      ))
                                    ) : (
                                      editQuery.length > 1 && (
                                        <div className="p-2 text-[11px] font-medium text-slate-400 text-center italic">
                                          No ports found for "{editQuery}"
                                        </div>
                                      )
                                    )}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div
                                className="port-box-pill clickable"
                                title="Click to edit port"
                                onClick={() => startEditingPort(loc, "sidebar")}
                              >
                                <DragIndicatorIcon style={{ fontSize: 14, color: '#9ca3af', cursor: 'grab', marginRight: 2 }} />
                                <span className="country-flag-badge">{countryCode}</span>
                                <span className="port-title-text">{displayName}</span>
                                <button
                                  type="button"
                                  className="port-box-remove"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemovePort(loc.id);
                                  }}
                                  title="Remove port"
                                >
                                  <CloseIcon style={{ fontSize: 13 }} />
                                </button>
                              </div>
                            )}

                            {/* Leg distance info between ports */}
                            {!isLast && (
                              <div className="leg-distance-metric">
                                {hasCalculatedRoute ? (
                                  <>
                                    <span>
                                      Normal <strong>{legDistNormal.toLocaleString()} nm</strong> · ECA <strong>{legDistEca.toLocaleString()} nm</strong>
                                    </span>
                                    {legDays > 0 && (
                                      <span className="via-badge-pill">
                                        {formatDays(legDays)}d · {getAvoidSuez ? "via Cape" : "via Suez"}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <span className="italic text-slate-400">Pending calculation</span>
                                )}
                              </div>
                            )}
                          </div>
                        </Reorder.Item>
                      );
                    })}

                    {addPortsArray.length < 2 && (
                      <div className="text-[11px] italic py-1 text-slate-400 font-medium">
                        Search and add at least 2 ports in the search bar above to calculate route.
                      </div>
                    )}
                  </Reorder.Group>
                </div>
              </div>
            </div>

            {/* Clear Divider between Ports and Options */}
            <div className="w-full bg-slate-200 shrink-0" style={{ height: '1.5px' }} />

            {/* Pinned Bottom Route Options & Parameters Section */}
            <div className="panel-footer" style={{ borderTop: 'none' }}>
              <div className="text-[11px] font-medium tracking-wider uppercase" style={{ color: "#475569" }}>
                ROUTE OPTIONS
              </div>

              {/* Canal Avoid Toggles */}
              <div className="flex items-center gap-2">
                <div
                  className={`signal-toggle-pill flex-1 justify-center ${getAvoidSuez ? "active" : ""
                    }`}
                  onClick={handleToggleSuez}
                  title="Toggle Avoid Suez canal"
                >
                  {getAvoidSuez && (
                    <CheckIcon
                      className="toggle-check-icon"
                      style={{ fontSize: 13, color: "#ffffff", fill: "#ffffff", flexShrink: 0 }}
                    />
                  )}
                  <span>Avoid Suez</span>
                </div>
                <div
                  className={`signal-toggle-pill flex-1 justify-center ${getAvoidPanama ? "active" : ""
                    }`}
                  onClick={handleTogglePanama}
                  title="Toggle Avoid Panama canal"
                >
                  {getAvoidPanama && (
                    <CheckIcon
                      className="toggle-check-icon"
                      style={{ fontSize: 13, color: "#ffffff", fill: "#ffffff", flexShrink: 0 }}
                    />
                  )}
                  <span>Avoid Panama</span>
                </div>
              </div>

              {/* Speed */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 text-[11px] font-medium" style={{ color: "#475569" }}>
                  <SpeedIcon style={{ fontSize: 14, color: "#9ca3af" }} />
                  <span>VESSEL SPEED</span>
                </div>
                <div className="signal-control-stepper">
                  <button
                    type="button"
                    className="step-btn"
                    onClick={() => handleSpeedStep(-0.5)}
                  >
                    -
                  </button>
                  <input
                    type="number"
                    className="step-input"
                    value={speed}
                    onChange={(e) => setSpeed(e.target.value)}
                    step="0.5"
                    min="1"
                  />
                  <span className="step-unit">kts</span>
                  <button
                    type="button"
                    className="step-btn"
                    onClick={() => handleSpeedStep(0.5)}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Sea Margin */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 text-[11px] font-medium" style={{ color: "#475569" }}>
                  <WavesIcon style={{ fontSize: 14, color: "#9ca3af" }} />
                  <span>SEA MARGIN</span>
                </div>
                <div className="signal-control-stepper">
                  <button
                    type="button"
                    className="step-btn"
                    onClick={() => handleMarginStep(-1.0)}
                  >
                    -
                  </button>
                  <input
                    type="number"
                    className="step-input"
                    value={seaMargin}
                    onChange={(e) => setSeaMargin(e.target.value)}
                    step="1"
                    min="0"
                  />
                  <span className="step-unit">%</span>
                  <button
                    type="button"
                    className="step-btn"
                    onClick={() => handleMarginStep(1.0)}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Primary Action Buttons */}
              <div className="pt-1 flex items-center gap-2">
                <button
                  type="button"
                  className="flex-1 h-8 py-1 px-3 rounded-md text-white font-medium text-[11px] tracking-wide transition-all flex items-center justify-center gap-1.5 disabled:cursor-not-allowed"
                  style={{ background: "#1a2332" }}
                  onMouseEnter={(e) => {
                    if (!e.currentTarget.disabled) e.currentTarget.style.background = "#111827";
                  }}
                  onMouseLeave={(e) => {
                    if (!e.currentTarget.disabled) e.currentTarget.style.background = "#1a2332";
                  }}
                  onClick={handleRouteCalculation}
                  disabled={validPortsCount < 2 || loader}
                >
                  {loader ? (
                    <>
                      <CircularProgress color="inherit" size={12} />
                      <span>Calculating...</span>
                    </>
                  ) : (
                    <span>{hasCalculatedRoute ? "Update Route" : "Show Route"}</span>
                  )}
                </button>

                {hasCalculatedRoute && (
                  <button
                    type="button"
                    className="h-8 py-1 px-3 rounded-md text-[11px] font-medium transition-colors"
                    style={{ border: "1.5px solid #d1d5db", color: "#374151", background: "#ffffff" }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#f9fafb";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#ffffff";
                    }}
                    onClick={handleResetRoute}
                    title="Reset Route"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Right Column: Top Port Input Bar + Map */}
      <div className="flex-1 h-full flex flex-col overflow-hidden relative z-0">
        {topTabs}

        {/* Map Container (Takes remaining space on right) */}
        <div className="flex-1 h-full relative overflow-hidden">
          {/* Dedicated Top Port Selection Bar (Signal Ocean Exact Style - overlaid on map) */}
          <div className="signal-ocean-top-bar">
            <div className="signal-ocean-unified-bar">
              <div className="bar-search-icon">
                <SearchIcon style={{ fontSize: 15 }} />
              </div>

              {/* Chips Container with Animated Framer Motion Drag-and-Drop */}
              <Reorder.Group
                axis="x"
                values={addPortsArray}
                onReorder={handleReorder}
                as="div"
                className="chips-container"
              >
                {addPortsArray.map((loc) => {
                  const isEditingThis = editingPortId === loc.id && editingSource === "top";

                  if (isEditingThis) {
                    return (
                      <div
                        key={loc.id}
                        ref={editChipContainerRef}
                        className="signal-port-chip is-editing"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="chip-pin">
                          <RoomIcon style={{ fontSize: 13, color: "#2563eb" }} />
                        </span>
                        <input
                          ref={editInputRef}
                          type="text"
                          className="chip-edit-input"
                          value={editQuery}
                          onChange={(e) => onEditSearchInput(e.target.value)}
                          onFocus={() => {
                            if (editSuggestions.length > 0) setIsEditDropdownOpen(true);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") {
                              cancelEditing();
                            } else if (e.key === "Enter" && editSuggestions.length > 0) {
                              handleUpdatePort(loc.id, editSuggestions[0]);
                            }
                          }}
                          placeholder="Search port..."
                        />
                        <button
                          type="button"
                          className="chip-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            cancelEditing();
                          }}
                          title="Cancel edit"
                        >
                          <CloseIcon style={{ fontSize: 11 }} />
                        </button>

                        {/* Autocomplete Dropdown anchored to editing chip */}
                        {isEditDropdownOpen && (
                          <div className="signal-ocean-suggestions-dropdown chip-dropdown">
                            {isEditSearching ? (
                              <div className="flex items-center gap-1.5 p-2 text-[11px] font-medium text-slate-500">
                                <CircularProgress size={12} color="inherit" />
                                <span>Searching ports...</span>
                              </div>
                            ) : editSuggestions.length > 0 ? (
                              editSuggestions.map((item, idx) => (
                                <div
                                  key={idx}
                                  className="suggestion-item"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleUpdatePort(loc.id, item);
                                  }}
                                >
                                  <RoomIcon style={{ fontSize: 13, color: "#64748b" }} />
                                  <span className="suggestion-port-name">{item.name}</span>
                                  {item.country && (
                                    <span className="suggestion-port-country">
                                      ({item.country})
                                    </span>
                                  )}
                                </div>
                              ))
                            ) : (
                              editQuery.length > 1 && (
                                <div className="p-2 text-[11px] font-medium text-slate-400 text-center italic">
                                  No ports found for "{editQuery}"
                                </div>
                              )
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <Reorder.Item
                      key={loc.id}
                      value={loc}
                      as="div"
                      className="signal-port-chip"
                      whileDrag={{
                        scale: 1.04,
                        boxShadow: "0 4px 14px rgba(0, 0, 0, 0.14)",
                        borderColor: "#6b7280",
                        backgroundColor: "#f3f4f6",
                        borderRadius: 20,
                        zIndex: 100,
                        cursor: "grabbing",
                      }}
                      transition={{
                        type: "spring",
                        stiffness: 450,
                        damping: 30,
                        mass: 0.8,
                      }}
                      onDragEnd={handleDragEnd}
                      onClick={() => startEditingPort(loc, "top")}
                      title="Click to edit port • Drag to reorder"
                    >
                      <span className="chip-pin">
                        <RoomIcon style={{ fontSize: 13 }} />
                      </span>
                      <span className="chip-name">{loc.port}</span>
                      <button
                        type="button"
                        className="chip-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemovePort(loc.id);
                        }}
                        title="Remove port"
                      >
                        <CloseIcon style={{ fontSize: 11 }} />
                      </button>
                    </Reorder.Item>
                  );
                })}
              </Reorder.Group>

              {/* Inline Port Input & Dropdown */}
              <div className="inline-search-box">
                <input
                  ref={inputRef}
                  type="text"
                  className="inline-port-input"
                  value={searchQuery}
                  style={{ width: `${Math.max(searchQuery.length * 8 + 10, addPortsArray.length === 0 ? 170 : addPortsArray.length === 1 ? 160 : 120)}px` }}
                  onChange={(e) => onSearchInput(e.target.value)}
                  onFocus={() => {
                    if (searchSuggestions.length > 0) setIsDropdownOpen(true);
                  }}
                  placeholder={
                    addPortsArray.length === 0
                      ? "Search departure port..."
                      : addPortsArray.length === 1
                        ? "Add destination port..."
                        : "Add next port..."
                  }
                />

                {/* Autocomplete Dropdown */}
                {isDropdownOpen && (
                  <div className="signal-ocean-suggestions-dropdown">
                    {isSearching ? (
                      <div className="flex items-center gap-1.5 p-2 text-[11px] font-medium text-slate-500">
                        <CircularProgress size={12} color="inherit" />
                        <span>Searching ports...</span>
                      </div>
                    ) : searchSuggestions.length > 0 ? (
                      searchSuggestions.map((item, idx) => (
                        <div
                          key={idx}
                          className="suggestion-item"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleSelectPort(item);
                          }}
                        >
                          <RoomIcon style={{ fontSize: 13, color: "#64748b" }} />
                          <span className="suggestion-port-name">{item.name}</span>
                          {item.country && (
                            <span className="suggestion-port-country">
                              ({item.country})
                            </span>
                          )}
                        </div>
                      ))
                    ) : (
                      searchQuery.length > 1 && (
                        <div className="p-2 text-[11px] font-medium text-slate-400 text-center italic">
                          No ports found for "{searchQuery}"
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>

              {/* Clear All action on far right */}
              <div className="bar-end-actions">
                {addPortsArray.length > 0 && (
                  <button
                    type="button"
                    className="clear-all-btn"
                    onClick={handleClearAll}
                    title="Clear all ports"
                  >
                    <CloseIcon style={{ fontSize: 14 }} />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="distance-matrix-map" id="map" />

          {/* Floating Top-Left Route Metrics Row (Compact Horizontal HUD on Map) */}
          <div className="maritime-floating-metrics-row" style={{ top: '64px' }}>
            {/* 1. Duration */}
            <div className="metric-pill-col">
              <span className="metric-pill-label">DURATION</span>
              <div className="metric-pill-body">
                <span className="metric-val-strong">
                  {hasCalculatedRoute ? formatDays(totalDays) : "0"}
                  <span className="unit">d</span>
                </span>
                <span className="metric-sub-detail">
                  {hasCalculatedRoute
                    ? formatDurationBreakdown(totalDays)
                    : `At ${speed || "13.0"} kts`}
                </span>
              </div>
            </div>

            <div className="metric-pill-divider" />

            {/* 2. Distance */}
            <div className="metric-pill-col">
              <span className="metric-pill-label">DISTANCE</span>
              <div className="metric-pill-body">
                <span className="metric-val-strong">
                  {hasCalculatedRoute ? totalDistance.toLocaleString() : "0"}
                  <span className="unit">NM</span>
                </span>
                <span className="metric-sub-detail">Total distance</span>
              </div>
            </div>

            <div className="metric-pill-divider" />

            {/* 3. (S)ECA */}
            <div className="metric-pill-col">
              <span className="metric-pill-label">
                <span className="metric-dot eca" />
                (S)ECA
              </span>
              <div className="metric-pill-body">
                <span className="metric-val-strong">
                  {hasCalculatedRoute ? totalDistEca.toLocaleString() : "0"}
                  <span className="unit">NM</span>
                </span>
                <span className="metric-sub-detail">
                  {hasCalculatedRoute
                    ? `${formatDays(totalDaysEca)} d (${ecaPercent}%)`
                    : "0 d (0%)"}
                </span>
              </div>
            </div>

            <div className="metric-pill-divider" />

            {/* 4. Non-ECA */}
            <div className="metric-pill-col">
              <span className="metric-pill-label">
                <span className="metric-dot noneca" />
                NON-ECA
              </span>
              <div className="metric-pill-body">
                <span className="metric-val-strong">
                  {hasCalculatedRoute ? totalDistNonEca.toLocaleString() : "0"}
                  <span className="unit">NM</span>
                </span>
                <span className="metric-sub-detail">
                  {hasCalculatedRoute
                    ? `${formatDays(totalDaysNonEca)} d (${nonEcaPercent}%)`
                    : "0 d (0%)"}
                </span>
              </div>
            </div>
          </div>

          {/* Floating Quick Action: Fit Route Overview */}
          {hasCalculatedRoute && (
            <div className="maritime-map-floating-actions">
              <button
                type="button"
                className="map-recenter-btn"
                onClick={handleFitRouteBounds}
                title="Fit entire voyage route on screen"
              >
                <CenterFocusStrongIcon style={{ fontSize: 15 }} />
                <span>Fit Route</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
